/**
 * Dynamic spacing tokens.
 *
 * Numeric spacing steps are the canonical spacing contract.
 * Numeric values follow the formula `n * 0.25rem`.
 *
 * @example
 * ```typescript
 * spacing[4]   // '1rem'
 * spacing[7]   // '1.75rem'
 * ```
 *
 * @module @smoothie-framework/ui-core/tokens
 */
/** Base unit multiplier: each spacing unit = 0.25rem */
const SPACING_UNIT_REM = 0.25;
export const POSITIVE_SPACING_STEPS = [
    0,
    0.5,
    1,
    1.5,
    2,
    2.5,
    3,
    3.5,
    4,
    4.5,
    5,
    5.5,
    6,
    6.5,
    7,
    7.5,
    8,
    8.5,
    9,
    10,
    11,
    12,
    13,
    14,
    15,
    16,
    20,
    24,
    25,
    28,
    32,
    36,
    37.5,
    40,
    44,
    48,
    50,
    52,
    56,
    60,
    64,
    72,
    80,
    96,
];
export const NEGATIVE_SPACING_STEPS = [
    -0.5,
    -1,
    -1.5,
    -2,
    -2.5,
    -3,
    -3.5,
    -4,
    -4.5,
    -5,
    -6,
    -7,
    -8,
    -9,
    -10,
    -11,
    -12,
    -13,
    -14,
    -15,
    -16,
    -20,
    -24,
    -28,
    -32,
    -36,
    -40,
    -44,
    -48,
    -52,
    -56,
    -60,
    -64,
    -72,
    -80,
    -96,
];
/**
 * Resolve a spacing number to its rem value string.
 * Pure computation, no side effects.
 */
export function spacingValue(n) {
    const rem = n * SPACING_UNIT_REM;
    return `${rem}rem`;
}
export function isSupportedSpacingStep(value) {
    return Number.isFinite(value);
}
/**
 * Dynamic spacing scale backed by a Proxy.
 *
 * Every numeric property access computes `n * 0.25rem` on the fly.
 * No cache, no predefined numeric keys, no limits.
 */
export const spacing = new Proxy(Object.create(null), {
    get(_target, prop) {
        if (typeof prop === 'symbol')
            return undefined;
        const n = Number(prop);
        if (!isSupportedSpacingStep(n))
            return undefined;
        return spacingValue(n);
    },
    has(_target, prop) {
        if (typeof prop === 'symbol')
            return false;
        const n = Number(prop);
        return isSupportedSpacingStep(n);
    },
    ownKeys() {
        return [];
    },
    getOwnPropertyDescriptor(_target, prop) {
        if (typeof prop === 'symbol')
            return undefined;
        const n = Number(prop);
        if (!isSupportedSpacingStep(n))
            return undefined;
        return {
            configurable: true,
            enumerable: false,
            value: spacingValue(n),
        };
    },
});
/**
 * Common spacing steps used for token export and Tailwind preset generation.
 *
 * These are the well-known spacing values that will be serialized to binary,
 * Zig source, and Tailwind config. The dynamic Proxy accepts any number,
 * but exporters need a finite enumerable set.
 *
 * Matches the exported first-release spacing seed set.
 *
 * Runtime spacing authoring remains dynamic and accepts any finite numeric
 * step, but export surfaces need a finite, stable subset.
 */
export const COMMON_SPACING_STEPS = [
    ...POSITIVE_SPACING_STEPS,
    ...NEGATIVE_SPACING_STEPS,
];
/** Re-export the base unit for consumers that need raw computation */
export { SPACING_UNIT_REM };
//# sourceMappingURL=spacing.js.map